// src/pages/PurchaseStockManagement.js
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, MenuItem, Paper, Stack, Tab, Tabs, Table, TableBody, TableCell,
  TableHead, TableRow, TextField, Typography, Tooltip, RadioGroup, Radio, FormControlLabel
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { apiUrl } from '../config/api';
import AppDateField from '../components/inputs/AppDateField';
import { formatSriLankaDateLabel, getSriLankaTodayYmd } from '../lib/sriLankaTime';

// ====== API endpoints (adjust if your paths differ) ======
const PURCHASE_ITEMS_API   = apiUrl('purchase_items');
const STOCK_PURCHASES_API  = apiUrl('stock_purchases');
const STOCK_USAGE_API      = apiUrl('stock_usage');

// ====== helpers ======
const fmt = (n, d=2) => Number(n || 0).toLocaleString('en-US', {
  minimumFractionDigits: d, maximumFractionDigits: d
});
const toArray = (value) => (Array.isArray(value) ? value : []);

// Unit helpers
const toBaseQty = (unit, qty) => {
  const q = Number(qty || 0);
  if (unit === 'kg') return { base: 'g', qty: q * 1000 };
  if (unit === 'g')  return { base: 'g', qty: q };
  return { base: 'pc', qty: q }; // pieces
};
const toDisplay = (base, qty) => {
  if (base === 'g') {
    if (qty >= 1000) return { unit: 'kg', qty: qty / 1000 };
    return { unit: 'g', qty };
  }
  return { unit: 'pc', qty };
};

// Status colors
const DEFAULT_MIN_G  = 1000; // 1 kg
const DEFAULT_MIN_PC = 5;
const statusFromAvailable = (base, availableQty) => {
  if (availableQty <= 0) return { label:'Out', color:'error' };
  const min = base === 'g' ? DEFAULT_MIN_G : DEFAULT_MIN_PC;
  if (availableQty < min)  return { label:'Low', color:'warning' };
  return { label:'OK', color:'success' };
};

// ===================== Purchase Edit Dialog =====================
function PurchaseEditDialog({ open, onClose, initial, items, onSaved }) {
  const categories = useMemo(
    () => Array.from(new Set(items.map(i => i.category))).sort((a,b)=>a.localeCompare(b)),
    [items]
  );
  const [form, setForm] = useState({
    id: null,
    purchase_date: getSriLankaTodayYmd(),
    category: "",
    item_id: "",
    unit: "kg",
    qty: "",
    total_amount: "",
    vendor: "",
    notes: ""
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (initial) {
      setForm({
        id: initial.id ?? null,
        purchase_date: initial.purchase_date || getSriLankaTodayYmd(),
        category: initial.category || "",
        item_id: initial.item_id ? String(initial.item_id) : "",
        unit: initial.unit || "kg",
        qty: String(initial.qty ?? ""),
        total_amount: String(initial.total_amount ?? ""),
        vendor: initial.vendor || "",
        notes: initial.notes || ""
      });
    } else {
      setForm({
        id: null,
        purchase_date: getSriLankaTodayYmd(),
        category: "",
        item_id: "",
        unit: "kg",
        qty: "",
        total_amount: "",
        vendor: "",
        notes: ""
      });
    }
  }, [open, initial]);

  const filteredItems = form.category ? items.filter(i => i.category === form.category) : [];
  const selectedItem  = filteredItems.find(i => String(i.id) === String(form.item_id));

  const save = async () => {
    if (!form.purchase_date) return alert("Pick a date");
    if (!form.category)      return alert("Pick a category");
    if (!form.item_id)       return alert("Pick an item");
    const qty = parseFloat(form.qty);
    if (!(qty>0))            return alert("Enter a valid quantity");
    const total = parseFloat(form.total_amount);
    if (!(total>=0))         return alert("Enter a valid total amount");

    const payload = {
      action: form.id ? 'update' : 'add',
      id: form.id || undefined,
      purchase_date: form.purchase_date,
      item_id: parseInt(form.item_id,10),
      category: form.category,
      item_name: selectedItem?.name ?? "",
      unit: form.unit,
      qty,
      total_amount: total,
      vendor: form.vendor || null,
      notes: form.notes || null
    };

    setBusy(true);
    try {
      const res = await fetch(STOCK_PURCHASES_API, {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify(payload)
      }).then(r=>r.json());
      if (res.status !== 'ok' && res.status !== 'success') {
        alert(res.message || 'Save failed'); return;
      }
      onSaved();
      onClose();
    } catch { alert('Network error'); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onClose={busy?undefined:onClose} fullWidth>
      <DialogTitle>{form.id ? 'Edit Purchase' : 'Add Purchase'}</DialogTitle>
      <DialogContent dividers sx={{ display:'flex', flexDirection:'column', gap:2 }}>
        <AppDateField label="Purchase Date" helperText="DD/MM/YYYY"
          value={form.purchase_date} onChange={e=>setForm(f=>({...f, purchase_date:e.target.value}))}/>
        <TextField select label="Category" value={form.category}
          onChange={e=>setForm(f=>({...f, category:e.target.value, item_id:''}))}>
          {categories.map(c=> <MenuItem key={c} value={c}>{c}</MenuItem>)}
        </TextField>
        <TextField select label="Item" value={form.item_id} disabled={!form.category}
          onChange={e=>setForm(f=>({...f, item_id:e.target.value}))}>
          {filteredItems.map(i => <MenuItem key={i.id} value={i.id}>{i.name}</MenuItem>)}
        </TextField>

        <Box>
          <Typography variant="subtitle2" gutterBottom>Unit</Typography>
          <RadioGroup row value={form.unit} onChange={e=>setForm(f=>({...f, unit:e.target.value}))}>
            <FormControlLabel value="kg" control={<Radio/>} label="Kg" />
            <FormControlLabel value="g"  control={<Radio/>} label="Gram (g)" />
            <FormControlLabel value="pc" control={<Radio/>} label="Piece" />
          </RadioGroup>
        </Box>

        <Stack direction="row" spacing={2}>
          <TextField label="Quantity" type="number" value={form.qty}
            onChange={e=>setForm(f=>({...f, qty:e.target.value}))} sx={{ flex:1 }}/>
          <TextField label="Total Amount (Rs.)" type="number" value={form.total_amount}
            onChange={e=>setForm(f=>({...f, total_amount:e.target.value}))} sx={{ flex:1 }}/>
        </Stack>
        {!!(parseFloat(form.qty)>0 && parseFloat(form.total_amount)>=0) && (
          <Typography variant="caption" color="text.secondary">
            Unit Price&nbsp;
            <strong>
              Rs {fmt((parseFloat(form.total_amount)||0) / (parseFloat(form.qty)||1))}
              / {form.unit}
            </strong>
          </Typography>
        )}
        <TextField label="Vendor (optional)" value={form.vendor}
          onChange={e=>setForm(f=>({...f, vendor:e.target.value}))}/>
        <TextField label="Notes (optional)" value={form.notes}
          onChange={e=>setForm(f=>({...f, notes:e.target.value}))}/>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button onClick={save} disabled={busy} variant="contained">
          {busy ? 'Saving…' : (form.id ? 'Update' : 'Add')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ===================== Usage Edit Dialog =====================
function UsageEditDialog({ open, onClose, initial, items, onSaved }) {
  const categories = useMemo(
    () => Array.from(new Set(items.map(i => i.category))).sort((a,b)=>a.localeCompare(b)),
    [items]
  );
  const [form, setForm] = useState({
    id: null,
    used_date: getSriLankaTodayYmd(),
    category: "",
    item_id: "",
    unit: "kg",
    qty: "",
    notes: ""
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (initial) {
      setForm({
        id: initial.id ?? null,
        used_date: initial.used_date || getSriLankaTodayYmd(),
        category: initial.category || "",
        item_id: initial.item_id ? String(initial.item_id) : "",
        unit: initial.unit || "kg",
        qty: String(initial.qty ?? ""),
        notes: initial.notes || ""
      });
    } else {
      setForm({
        id: null,
        used_date: getSriLankaTodayYmd(),
        category: "",
        item_id: "",
        unit: "kg",
        qty: "",
        notes: ""
      });
    }
  }, [open, initial]);

  const filteredItems = form.category ? items.filter(i => i.category === form.category) : [];
  const selectedItem  = filteredItems.find(i => String(i.id) === String(form.item_id));

  const save = async () => {
    if (!form.used_date) return alert("Pick a date");
    if (!form.category)  return alert("Pick a category");
    if (!form.item_id)   return alert("Pick an item");
    const qty = parseFloat(form.qty);
    if (!(qty>0))        return alert("Enter a valid quantity");

    const payload = {
      action: form.id ? 'update' : 'add',
      id: form.id || undefined,
      used_date: form.used_date,
      item_id: parseInt(form.item_id,10),
      category: form.category,
      item_name: selectedItem?.name ?? "",
      unit: form.unit,
      qty,
      notes: form.notes || null
    };

    setBusy(true);
    try {
      const res = await fetch(STOCK_USAGE_API, {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify(payload)
      }).then(r=>r.json());
      if (res.status !== 'ok') { alert(res.message || 'Save failed'); return; }
      onSaved();
      onClose();
    } catch { alert('Network error'); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onClose={busy?undefined:onClose} fullWidth>
      <DialogTitle>{form.id ? 'Edit Usage' : 'Add Usage'}</DialogTitle>
      <DialogContent dividers sx={{ display:'flex', flexDirection:'column', gap:2 }}>
        <AppDateField label="Used Date" helperText="DD/MM/YYYY"
          value={form.used_date} onChange={e=>setForm(f=>({...f, used_date:e.target.value}))}/>
        <TextField select label="Category" value={form.category}
          onChange={e=>setForm(f=>({...f, category:e.target.value, item_id:''}))}>
          {categories.map(c=> <MenuItem key={c} value={c}>{c}</MenuItem>)}
        </TextField>
        <TextField select label="Item" value={form.item_id} disabled={!form.category}
          onChange={e=>setForm(f=>({...f, item_id:e.target.value}))}>
          {filteredItems.map(i => <MenuItem key={i.id} value={i.id}>{i.name}</MenuItem>)}
        </TextField>

        <Box>
          <Typography variant="subtitle2" gutterBottom>Unit</Typography>
          <RadioGroup row value={form.unit} onChange={e=>setForm(f=>({...f, unit:e.target.value}))}>
            <FormControlLabel value="kg" control={<Radio/>} label="Kg" />
            <FormControlLabel value="g"  control={<Radio/>} label="Gram (g)" />
            <FormControlLabel value="pc" control={<Radio/>} label="Piece" />
          </RadioGroup>
        </Box>

        <TextField label="Quantity" type="number" value={form.qty}
          onChange={e=>setForm(f=>({...f, qty:e.target.value}))}/>
        <TextField label="Notes (optional)" value={form.notes}
          onChange={e=>setForm(f=>({...f, notes:e.target.value}))}/>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button onClick={save} disabled={busy} variant="contained">
          {busy ? 'Saving…' : (form.id ? 'Update' : 'Add')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ===================== Main Page =====================
export default function PurchaseStockManagement() {
  const [tab, setTab] = useState(0);

  // Master items (category + item list)
  const [items, setItems] = useState([]);
  const categories = useMemo(
    () => Array.from(new Set(items.map(i => i.category))).sort((a,b)=>a.localeCompare(b)),
    [items]
  );

  // Purchases state
  const [pFrom, setPFrom] = useState('');
  const [pTo, setPTo]     = useState('');
  const [pCategory, setPCategory] = useState('');
  const [pItemId, setPItemId]     = useState('');
  const [purchases, setPurchases] = useState([]);
  const [purchLoading, setPurchLoading] = useState(false);
  const [pEditOpen, setPEditOpen] = useState(false);
  const [pInitial, setPInitial]   = useState(null);

  // Reports (aggregated from purchases)
  const repRows = useMemo(() => {
    const grp = new Map();
    purchases.forEach(r => {
      const key = `${r.category}|||${r.item_name}|||${r.unit}`;
      if (!grp.has(key)) grp.set(key, { category:r.category, item_name:r.item_name, unit:r.unit, qty:0, total:0 });
      const row = grp.get(key);
      row.qty   += Number(r.qty || 0);
      row.total += Number(r.total_amount || 0);
    });
    return Array.from(grp.values())
      .sort((a,b)=> a.category===b.category ? a.item_name.localeCompare(b.item_name) : a.category.localeCompare(b.category));
  }, [purchases]);

  // Stock & Usage filters (shared)
  const [sFrom, setSFrom] = useState('');
  const [sTo, setSTo]     = useState('');
  const [sCategory, setSCategory] = useState('');
  const [sItemId, setSItemId]     = useState('');
  const itemsOfSCategory = useMemo(
    () => sCategory ? items.filter(i => i.category === sCategory) : [],
    [items, sCategory]
  );

  // Stock (computed) & Usage lists
  const [stockRows, setStockRows] = useState([]);
  const [stockLoading, setStockLoading] = useState(false);

  const [usageRows, setUsageRows] = useState([]);
  const [usageLoading, setUsageLoading] = useState(false);
  const [usageOpen, setUsageOpen] = useState(false);
  const [usageInitial, setUsageInitial] = useState(null);

  // Loaders
  const loadItems = () => {
    fetch(PURCHASE_ITEMS_API)
      .then(r=>r.json())
      .then(d => setItems(
        Array.isArray(d?.items) ? d.items
          : Array.isArray(d?.data) ? d.data
            : Array.isArray(d) ? d
              : []
      ))
      .catch(()=> setItems([]));
  };

  const loadPurchases = () => {
    const q = new URLSearchParams();
    if (pFrom) q.append('date_from', pFrom);
    if (pTo)   q.append('date_to',   pTo);
    if (pCategory) q.append('category', pCategory);
    if (pItemId)   q.append('item_id',  pItemId);
    setPurchLoading(true);
    fetch(`${STOCK_PURCHASES_API}?${q.toString()}`)
      .then(r=>r.json())
      .then(d => setPurchases(toArray(d.items)))
      .catch(()=> setPurchases([]))
      .finally(()=> setPurchLoading(false));
  };

  const deletePurchase = async (row) => {
    if (!window.confirm(`Delete purchase #${row.id} (${row.category} > ${row.item_name}) ?`)) return;
    try {
      const res = await fetch(STOCK_PURCHASES_API, {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ action:'delete', id: row.id })
      }).then(r=>r.json());
      if (res.status !== 'ok' && res.status !== 'success') { alert(res.message || 'Delete failed'); return; }
      loadPurchases();
    } catch { alert('Network error'); }
  };

  const loadUsage = () => {
    const q = new URLSearchParams();
    if (sFrom) q.append('date_from', sFrom);
    if (sTo)   q.append('date_to',   sTo);
    if (sCategory) q.append('category', sCategory);
    if (sItemId)   q.append('item_id',  sItemId);
    setUsageLoading(true);
    fetch(`${STOCK_USAGE_API}?${q.toString()}`)
      .then(r=>r.json())
      .then(d => setUsageRows(toArray(d.items)))
      .catch(()=> setUsageRows([]))
      .finally(()=> setUsageLoading(false));
  };

  // Compute stock = purchases - usage (unit-normalized)
  const loadStockComputed = async () => {
    setStockLoading(true);
    try {
      const q = new URLSearchParams();
      if (sFrom) q.append('date_from', sFrom);
      if (sTo)   q.append('date_to',   sTo);
      if (sCategory) q.append('category', sCategory);
      if (sItemId)   q.append('item_id',  sItemId);

      const [pRes, uRes] = await Promise.all([
        fetch(`${STOCK_PURCHASES_API}?${q.toString()}`).then(r=>r.json()).catch(()=>({items:[]})),
        fetch(`${STOCK_USAGE_API}?${q.toString()}`).then(r=>r.json()).catch(()=>({items:[]}))
      ]);
      const pItems = toArray(pRes.items);
      const uItems = toArray(uRes.items);

      const agg = new Map();
      const ensure = (item_id, category, item_name, base) => {
        const key = `${item_id}|${base}`;
        if (!agg.has(key)) {
          agg.set(key, { item_id, category, item_name, base, purchased:0, used:0 });
        }
        return agg.get(key);
      };

      for (const r of pItems) {
        const { base, qty } = toBaseQty(r.unit, r.qty);
        const row = ensure(r.item_id, r.category, r.item_name, base);
        row.purchased += Number(qty || 0);
      }
      for (const r of uItems) {
        const { base, qty } = toBaseQty(r.unit, r.qty);
        const row = ensure(r.item_id, r.category, r.item_name, base);
        row.used += Number(qty || 0);
      }

      const rows = Array.from(agg.values()).map(r => {
        const available = r.purchased - r.used;
        const status    = statusFromAvailable(r.base, available);
        const purchD    = toDisplay(r.base, r.purchased);
        const usedD     = toDisplay(r.base, r.used);
        const availD    = toDisplay(r.base, available);
        return {
          item_id: r.item_id,
          category: r.category,
          item_name: r.item_name,
          display_unit: availD.unit, // unify on available's unit
          purchased: purchD.unit === 'kg' && availD.unit === 'g' ? purchD.qty*1000
                    : purchD.unit === 'g'  && availD.unit === 'kg' ? purchD.qty/1000
                    : purchD.qty,
          used:      usedD.unit  === 'kg' && availD.unit === 'g' ? usedD.qty*1000
                    : usedD.unit  === 'g'  && availD.unit === 'kg' ? usedD.qty/1000
                    : usedD.qty,
          available: availD.qty,
          status
        };
      }).sort((a,b)=> a.category===b.category ? a.item_name.localeCompare(b.item_name) : a.category.localeCompare(b.category));

      setStockRows(rows);
    } finally {
      setStockLoading(false);
    }
  };

  const refreshUsage = () => loadUsage();
  const refreshStock = () => loadStockComputed();

  const deleteUsage = async (row) => {
    if (!window.confirm(`Delete usage #${row.id} (${row.category} > ${row.item_name}) ?`)) return;
    try {
      const res = await fetch(STOCK_USAGE_API, {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ action:'delete', id: row.id })
      }).then(r=>r.json());
      if (res.status !== 'ok') { alert(res.message || 'Delete failed'); return; }
      // After delete, refresh both usage and stock
      refreshUsage();
      refreshStock();
    } catch { alert('Network error'); }
  };

  // Init
  useEffect(() => { loadItems(); }, []);
  useEffect(() => { loadPurchases(); }, [pFrom, pTo, pCategory, pItemId]);
  useEffect(() => { // initial load for stock & usage once
    refreshStock();
    refreshUsage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Derived for dropdowns (Purchases tab)
  const itemsOfPCategory = useMemo(
    () => pCategory ? items.filter(i => i.category === pCategory) : [],
    [items, pCategory]
  );

  // Totals (Purchases tab)
  const totalSpend = purchases.reduce((s,r)=> s + Number(r.total_amount||0), 0);
  const totalRows  = purchases.length;

  return (
    <Box sx={{ p:2 }}>
      <Typography variant="h4" gutterBottom>Purchase & Stock Management</Typography>

      <Tabs value={tab} onChange={(_,v)=>setTab(v)} variant="scrollable" sx={{ mb:2 }}>
        <Tab label="Purchases" />
        <Tab label="Reports" />
        <Tab label="Stock" />
        <Tab label="Usage" />
      </Tabs>

      {/* ===================== TAB 0: PURCHASES ===================== */}
      {tab === 0 && (
        <Box sx={{ display:'flex', flexDirection:'column', gap:2 }}>
          {/* Filters + Actions */}
          <Paper sx={{ p:2 }}>
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
              <AppDateField
                label="From" size="small"
                value={pFrom} onChange={e=>setPFrom(e.target.value)}
                helperText="DD/MM/YYYY"
              />
              <AppDateField
                label="To" size="small"
                value={pTo} onChange={e=>setPTo(e.target.value)}
                helperText="DD/MM/YYYY"
              />
              <TextField
                select size="small" label="Category" value={pCategory}
                onChange={e=>{ setPCategory(e.target.value); setPItemId(''); }}
                sx={{ minWidth:220 }}
              >
                <MenuItem value="">All</MenuItem>
                {categories.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </TextField>
              <TextField
                select size="small" label="Item" value={pItemId} disabled={!pCategory}
                onChange={e=>setPItemId(e.target.value)} sx={{ minWidth:240 }}
              >
                <MenuItem value="">All</MenuItem>
                {itemsOfPCategory.map(i => <MenuItem key={i.id} value={i.id}>{i.name}</MenuItem>)}
              </TextField>
              <Button variant="contained" startIcon={<RefreshIcon/>} onClick={loadPurchases}>
                Apply
              </Button>
              <Box sx={{ flexGrow:1 }} />
              <Button
                variant="contained"
                startIcon={<AddIcon/>}
                onClick={() => { setPInitial(null); setPEditOpen(true); }}
              >
                Add Purchase
              </Button>
            </Stack>
          </Paper>

          {/* Totals */}
          <Paper sx={{ p:2, display:'flex', gap:4, flexWrap:'wrap' }}>
            <Box>
              <Typography variant="subtitle2">Rows</Typography>
              <Typography variant="h6">{totalRows}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2">Total Spend</Typography>
              <Typography variant="h6">Rs {fmt(totalSpend)}</Typography>
            </Box>
          </Paper>

          {/* Purchases Table */}
          <Paper sx={{ p:2 }}>
            {purchLoading ? (
              <CircularProgress size={22}/>
            ) : purchases.length === 0 ? (
              <Typography>No purchases</Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width={64}>No.</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>Item</TableCell>
                    <TableCell>Unit</TableCell>
                    <TableCell align="right">Qty</TableCell>
                    <TableCell align="right">Total (Rs)</TableCell>
                    <TableCell>Vendor</TableCell>
                    <TableCell>Notes</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {purchases.map((r, index) => (
                    <TableRow key={r.id} hover>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>{formatSriLankaDateLabel(r.purchase_date)}</TableCell>
                      <TableCell>{r.category}</TableCell>
                      <TableCell>{r.item_name}</TableCell>
                      <TableCell><Chip size="small" label={r.unit}/></TableCell>
                      <TableCell align="right">{fmt(r.qty, r.unit==='pc'?0:(r.unit==='g'?0:3))}</TableCell>
                      <TableCell align="right">{fmt(r.total_amount)}</TableCell>
                      <TableCell>{r.vendor}</TableCell>
                      <TableCell>{r.notes}</TableCell>
                      <TableCell>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => { setPInitial(r); setPEditOpen(true); }}>
                            <EditIcon fontSize="small"/>
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error" onClick={() => deletePurchase(r)}>
                            <DeleteIcon fontSize="small"/>
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Paper>

          {/* Purchase Modal */}
          <PurchaseEditDialog
            open={pEditOpen}
            onClose={()=>setPEditOpen(false)}
            initial={pInitial}
            items={items}
            onSaved={loadPurchases}
          />
        </Box>
      )}

      {/* ===================== TAB 1: REPORTS ===================== */}
      {tab === 1 && (
        <Box sx={{ display:'flex', flexDirection:'column', gap:2 }}>
          {/* Filters (reuse purchases filters) */}
          <Paper sx={{ p:2 }}>
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
              <AppDateField
                label="From" size="small"
                value={pFrom} onChange={e=>setPFrom(e.target.value)}
                helperText="DD/MM/YYYY"
              />
              <AppDateField
                label="To" size="small"
                value={pTo} onChange={e=>setPTo(e.target.value)}
                helperText="DD/MM/YYYY"
              />
              <TextField
                select size="small" label="Category" value={pCategory}
                onChange={e=>{ setPCategory(e.target.value); setPItemId(''); }}
                sx={{ minWidth:220 }}
              >
                <MenuItem value="">All</MenuItem>
                {categories.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </TextField>
              <TextField
                select size="small" label="Item" value={pItemId} disabled={!pCategory}
                onChange={e=>setPItemId(e.target.value)} sx={{ minWidth:240 }}
              >
                <MenuItem value="">All</MenuItem>
                {items.filter(i => !pCategory || i.category===pCategory).map(i => (
                  <MenuItem key={i.id} value={i.id}>{i.name}</MenuItem>
                ))}
              </TextField>
              <Button variant="contained" startIcon={<RefreshIcon/>} onClick={loadPurchases}>
                Apply
              </Button>
            </Stack>
          </Paper>

          {/* Aggregated table */}
          <Paper sx={{ p:2 }}>
            {repRows.length === 0 ? (
              <Typography>No data.</Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width={64}>No.</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>Item</TableCell>
                    <TableCell>Unit</TableCell>
                    <TableCell align="right">Total Qty</TableCell>
                    <TableCell align="right">Total Amount (Rs)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {repRows.map((r, i) => (
                    <TableRow key={i}>
                      <TableCell>{i + 1}</TableCell>
                      <TableCell>{r.category}</TableCell>
                      <TableCell>{r.item_name}</TableCell>
                      <TableCell><Chip size="small" label={r.unit}/></TableCell>
                      <TableCell align="right">{fmt(r.unit==='pc'?r.qty:Math.round(r.qty*1000)/1000, r.unit==='pc'?0:3)}</TableCell>
                      <TableCell align="right">{fmt(r.total)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Paper>
        </Box>
      )}

      {/* ===================== TAB 2: STOCK ===================== */}
      {tab === 2 && (
        <Box sx={{ display:'flex', flexDirection:'column', gap:2 }}>
          {/* Filters + Apply */}
          <Paper sx={{ p:2 }}>
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
              <AppDateField
                label="From" size="small"
                value={sFrom} onChange={e=>setSFrom(e.target.value)}
                helperText="DD/MM/YYYY"
              />
              <AppDateField
                label="To" size="small"
                value={sTo} onChange={e=>setSTo(e.target.value)}
                helperText="DD/MM/YYYY"
              />
              <TextField
                select size="small" label="Category" value={sCategory}
                onChange={e=>{ setSCategory(e.target.value); setSItemId(''); }}
                sx={{ minWidth:220 }}
              >
                <MenuItem value="">All</MenuItem>
                {categories.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </TextField>
              <TextField
                select size="small" label="Item" value={sItemId}
                onChange={e=>setSItemId(e.target.value)} sx={{ minWidth:240 }} disabled={!sCategory}
              >
                <MenuItem value="">All</MenuItem>
                {itemsOfSCategory.map(i => <MenuItem key={i.id} value={i.id}>{i.name}</MenuItem>)}
              </TextField>

              <Button variant="contained" startIcon={<RefreshIcon/>} onClick={refreshStock}>
                Apply
              </Button>
            </Stack>
          </Paper>

          {/* Available Stock (Purchases − Usage) */}
          <Paper sx={{ p:2 }}>
            <Typography variant="h6" sx={{ mb:1 }}>Available Stock (Purchases − Usage)</Typography>
            {stockLoading ? (
              <CircularProgress size={22}/>
            ) : stockRows.length === 0 ? (
              <Typography>No data.</Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width={64}>No.</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>Item</TableCell>
                    <TableCell align="right">Purchased</TableCell>
                    <TableCell align="right">Used</TableCell>
                    <TableCell align="right">Available</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stockRows.map((r, i) => (
                    <TableRow key={i} hover>
                      <TableCell>{i + 1}</TableCell>
                      <TableCell>{r.category}</TableCell>
                      <TableCell>{r.item_name}</TableCell>
                      <TableCell align="right">
                        {fmt(r.purchased, r.display_unit==='pc'?0:2)} {r.display_unit}
                      </TableCell>
                      <TableCell align="right">
                        {fmt(r.used, r.display_unit==='pc'?0:2)} {r.display_unit}
                      </TableCell>
                      <TableCell align="right" style={{ fontWeight:700 }}>
                        {fmt(r.available, r.display_unit==='pc'?0:2)} {r.display_unit}
                      </TableCell>
                      <TableCell>
                        <Chip size="small" color={r.status.color} label={r.status.label} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Paper>
        </Box>
      )}

      {/* ===================== TAB 3: USAGE ===================== */}
      {tab === 3 && (
        <Box sx={{ display:'flex', flexDirection:'column', gap:2 }}>
          {/* Filters + Actions */}
          <Paper sx={{ p:2 }}>
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
              <AppDateField
                label="From" size="small"
                value={sFrom} onChange={e=>setSFrom(e.target.value)}
                helperText="DD/MM/YYYY"
              />
              <AppDateField
                label="To" size="small"
                value={sTo} onChange={e=>setSTo(e.target.value)}
                helperText="DD/MM/YYYY"
              />
              <TextField
                select size="small" label="Category" value={sCategory}
                onChange={e=>{ setSCategory(e.target.value); setSItemId(''); }}
                sx={{ minWidth:220 }}
              >
                <MenuItem value="">All</MenuItem>
                {categories.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </TextField>
              <TextField
                select size="small" label="Item" value={sItemId}
                onChange={e=>setSItemId(e.target.value)} sx={{ minWidth:240 }} disabled={!sCategory}
              >
                <MenuItem value="">All</MenuItem>
                {itemsOfSCategory.map(i => <MenuItem key={i.id} value={i.id}>{i.name}</MenuItem>)}
              </TextField>

              <Button variant="contained" startIcon={<RefreshIcon/>} onClick={refreshUsage}>
                Apply
              </Button>
              <Box sx={{ flexGrow:1 }} />
              <Button variant="contained" startIcon={<AddIcon/>}
                onClick={()=>{ setUsageInitial(null); setUsageOpen(true); }}>
                Add Usage
              </Button>
            </Stack>
          </Paper>

          {/* Usage History */}
          <Paper sx={{ p:2 }}>
            <Typography variant="h6" sx={{ mb:1 }}>Usage History</Typography>
            {usageLoading ? (
              <CircularProgress size={22}/>
            ) : usageRows.length === 0 ? (
              <Typography>No usage recorded.</Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width={64}>No.</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>Item</TableCell>
                    <TableCell>Unit</TableCell>
                    <TableCell align="right">Qty</TableCell>
                    <TableCell>Notes</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {usageRows.map((r, index) => (
                    <TableRow key={r.id} hover>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>{formatSriLankaDateLabel(r.used_date)}</TableCell>
                      <TableCell>{r.category}</TableCell>
                      <TableCell>{r.item_name}</TableCell>
                      <TableCell><Chip size="small" label={r.unit}/></TableCell>
                      <TableCell align="right">{fmt(r.qty, r.unit==='pc'?0:(r.unit==='g'?0:2))}</TableCell>
                      <TableCell>{r.notes}</TableCell>
                      <TableCell>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => { setUsageInitial(r); setUsageOpen(true); }}>
                            <EditIcon fontSize="small"/>
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error" onClick={() => deleteUsage(r)}>
                            <DeleteIcon fontSize="small"/>
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Paper>

          {/* Usage Modal */}
          <UsageEditDialog
            open={usageOpen}
            onClose={()=>setUsageOpen(false)}
            initial={usageInitial}
            items={items}
            onSaved={() => { refreshUsage(); refreshStock(); }}
          />
        </Box>
      )}
    </Box>
  );
}
